import { forwardRef, type ReactNode, type KeyboardEvent, type MouseEvent } from 'react'
import { cn } from '../../lib/utils'

export interface TabActionButtonProps extends React.HTMLAttributes<HTMLSpanElement> {
	onClick?:   () => void
	ariaLabel?: string
	className?: string
	// Optional: when used as a `render` target (e.g. DropdownMenuTrigger), the
	// host primitive injects children at runtime, so the JSX literal may omit them.
	children?:  ReactNode
}

/**
 * Pill-shaped action button rendered next to a tab label inside a `TabsTrigger`.
 * Only visible when the tab is active (via `group-data-[state=active]/tab`).
 *
 * Forward-ref + spreads props so it can be used as a `render` target for
 * primitives like `<DropdownMenuTrigger render={<TabActionButton>...</TabActionButton>} />`.
 */
const TabActionButton = forwardRef<HTMLSpanElement, TabActionButtonProps>(
	({ onClick, ariaLabel, className, children, ...rest }, ref) => {
		const fire = (e: MouseEvent | KeyboardEvent) => {
			e.stopPropagation()
			e.preventDefault()
			onClick?.()
		}
		return (
			<span
				ref={ref}
				role="button"
				aria-label={ariaLabel}
				tabIndex={0}
				onClick={onClick ? fire : undefined}
				onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') fire(e) } : undefined}
				className={cn(
					'ml-1 flex size-7 items-center justify-center rounded-(--radius-sm) bg-(--color-secondary) opacity-0 pointer-events-none transition-opacity group-data-[state=active]/tab:opacity-100 group-data-[state=active]/tab:pointer-events-auto hover:bg-(--color-secondary-hover) cursor-pointer',
					className,
				)}
				{...rest}
			>
				{children}
			</span>
		)
	},
)

TabActionButton.displayName = 'TabActionButton'

export { TabActionButton }
